import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Lock, Loader2 } from "lucide-react";
import { API, errorText } from "../lib/site";

const PRESETS = [
  { v: 3, label: "A coffee" },
  { v: 5, label: "A chapter" },
  { v: 10, label: "A long night of writing" },
  { v: 25, label: "A patron" },
];

const MONTHLY_AMOUNTS = [5, 10, 15, 25];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block font-mono-d text-[11px] tracking-[0.2em] uppercase text-[#c4bbb0] mb-2">{label}</span>
    {children}
  </label>
);

const inputCls = "w-full bg-[#121016] border border-[#f4ede2]/15 rounded-xl px-4 py-3 text-[#f4ede2] placeholder:text-[#8f877e] focus:outline-none focus:border-[#e3ba73] transition-colors duration-300";

export const TipForm = () => {
  const [preset, setPreset] = useState(5);
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [monthly, setMonthly] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const amount = custom !== "" && !monthly ? Number(custom) : preset;
  const valid = amount >= 1 && amount <= 1000;

  const switchFrequency = (m) => {
    setMonthly(m);
    setPreset(5);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return setError("Choose an amount between $1 and $1,000.");
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/tips/checkout`, { amount: String(amount), name, message, public: isPublic, frequency: monthly ? "monthly" : "once" });
      window.location.assign(data.checkout_url);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="tip-form" className="relative rounded-[28px] border border-[#e3ba73]/20 bg-[#1c1824]/90 backdrop-blur-xl p-6 sm:p-9 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.7)]">
      <div data-testid="frequency-toggle" className="relative grid grid-cols-2 rounded-full border border-[#f4ede2]/15 p-1 mb-7">
        <motion.span layout transition={{ type: "spring", stiffness: 380, damping: 32 }}
          className={`absolute inset-y-1 w-[calc(50%-4px)] rounded-full bg-[#e3ba73] ${monthly ? "right-1" : "left-1"}`} />
        {[[false, "One-time"], [true, "Monthly"]].map(([m, label]) => (
          <button key={label} type="button" data-testid={`frequency-${m ? "monthly" : "once"}`} onClick={() => switchFrequency(m)}
            className={`relative z-10 py-2.5 text-sm font-semibold transition-colors duration-300 ${monthly === m ? "text-[#121016]" : "text-[#c4bbb0]"}`}>
            {label}
          </button>
        ))}
      </div>
      <p className="overline mb-5">01 — Choose an amount</p>
      <div className="grid grid-cols-2 gap-3">
        {PRESETS.map((p0, idx) => {
          const p = monthly ? { ...p0, v: MONTHLY_AMOUNTS[idx] } : p0;
          const active = (monthly || custom === "") && preset === p.v;
          return (
            <motion.button key={p.v} type="button" whileTap={{ scale: 0.96 }} data-testid={`tip-option-${p.v}`}
              onClick={() => { setPreset(p.v); setCustom(""); }}
              className={`relative text-left rounded-2xl border p-4 sm:p-5 transition-colors duration-300 ${active ? "border-[#e3ba73] bg-[#e3ba73] text-[#121016]" : "border-[#f4ede2]/15 hover:border-[#e3ba73]/60"}`}>
              <span className="block font-serif-d text-4xl font-semibold leading-none">${p.v}{monthly && <span className="text-lg font-normal">/mo</span>}</span>
              <span className={`block mt-2 text-xs ${active ? "text-[#121016]/80" : "text-[#c4bbb0]"}`}>{p.label}</span>
            </motion.button>
          );
        })}
      </div>
      {monthly ? (
        <p data-testid="monthly-note" className="mt-4 text-sm text-[#c4bbb0]">Billed monthly by Square until you cancel. Monthly support is offered at the four amounts above.</p>
      ) : (
      <div className="mt-4 relative">
        <span className="absolute left-4 top-1/2 -translate-y-1/2 font-serif-d text-2xl text-[#e3ba73]">$</span>
        <input data-testid="custom-tip-input" inputMode="decimal" type="number" min="1" max="1000" step="0.01" value={custom}
          onChange={(e) => setCustom(e.target.value)} placeholder="Or any amount you like"
          className={`${inputCls} pl-10 py-4 text-lg ${custom !== "" ? "border-[#e3ba73]" : ""}`} />
      </div>
      )}

      <p className="overline mt-9 mb-5">02 — Say hello (optional)</p>
      <div className="space-y-4">
        <Field label="Your name">
          <input data-testid="reader-name-input" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} placeholder="Leave blank to stay anonymous" className={inputCls} />
        </Field>
        <Field label="A note for Chris">
          <textarea data-testid="reader-message-input" maxLength={500} rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="What did the writing mean to you?" className={`${inputCls} resize-none`} />
        </Field>
        <label className="flex items-center gap-3 text-sm text-[#c4bbb0] cursor-pointer">
          <input data-testid="reader-public-checkbox" type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} className="w-4 h-4 accent-[#e3ba73]" />
          Show my name and note on the reader wall
        </label>
      </div>

      {error && <p data-testid="tip-error" className="mt-5 text-sm text-[#ff9b8a]">{error}</p>}

      <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }} data-testid="continue-to-square-button" disabled={busy}
        className="mt-8 w-full rounded-full bg-[#e3ba73] text-[#121016] py-4 font-semibold text-base flex items-center justify-center gap-3 disabled:opacity-70 hover:bg-[#f4d79e] transition-colors duration-300">
        {busy ? <Loader2 className="animate-spin" size={18} /> : <Lock size={16} />}
        {busy ? "Opening secure checkout…" : monthly ? `Start $${amount}/month on Square` : `Continue to Square — $${valid ? amount : "–"}`}
      </motion.button>
      <p className="mt-4 text-center text-xs text-[#c4bbb0]">You'll pay on Square's secure checkout page. Card details never touch this site.</p>
    </form>
  );
};
